import React, { useContext, useEffect, useMemo, useState } from "react";
import { useRouter } from "next/router";

import useMediaQuery from "@vahak/core-ui/dist/hooks/useMediaQuery";
import { scrollToElementById } from "@vahak/core-ui/dist/methods";
import { GA4EventNames } from "@vahak/core/dist/constants/event-names";
import { AppContext } from "@vahak/core/dist/app-context";

import ArrowRightGrey from "@vahak/core/dist/icons/ArrowRightGrey.svg";
import HomepageV2Animate from "../../../../homepage-v2/animate/HomepageV2Animate";
import HomepageV2SectionCard from "../../../../homepage-v2/section-card/HomepageV2SectionCard";
import AvailableLoadsGrid from "../../../../homepage-v2/available-loads-section/AvailableLoadsGrid";
import { getMktContentByCity } from "../../../../marketplace/content/content";
import { MarketplaceContext } from "../../../../../MarketplaceContextProvider";
import { useGetLoadMarketInfiniteQueryNew } from "../../../../../services/load/useGetLoadMarketV2/useGetLoadMarketInfiniteQueryNew";
import { LoadMarketCard, LoadMarketParam } from "../../../../../services/load/useGetLoadMarketV2/type";
import { IEventTrackerProp } from "../../../../../types/events";
import { MARKETPLACE_V3_LOADS_GRID, MARKETPLACE_V3_LOADS_SECTION_ID } from "../marketplaceV3.constants";
import useMarketplaceV3Translation from "../useMarketplaceV3Translation";
import SectionStyles from "../../../../homepage-v2/available-loads-section/AvailableLoadsSectionV2.module.scss";
import MarketplaceV3NoLoadsEmptyState from "./MarketplaceV3NoLoadsEmptyState";
import Styles from "./MarketplaceV3LoadsSection.module.scss";

type MarketplaceV3LoadsSectionProps = IEventTrackerProp & {
    onBidNow: (load: LoadMarketCard, rank: number) => void;
    inCityTextWithoutIndia: string;
};

const MarketplaceV3LoadsSection = ({
    trackEvent,
    eventData,
    onBidNow,
    inCityTextWithoutIndia
}: MarketplaceV3LoadsSectionProps) => {
    const { t } = useMarketplaceV3Translation();
    const router = useRouter();
    const isMobileScreen = useMediaQuery({ queryType: "mobile" });
    const { marketplaceConfig, shouldExecuteApi, setShouldExecuteApi } = useContext(MarketplaceContext);
    const { isPhoneVerified, companyId, setShouldPreventLayoutSwitch } = useContext(AppContext);
    const titleH1Desc = getMktContentByCity(inCityTextWithoutIndia, true).titleH1Desc;

    const pageSize = isMobileScreen
        ? MARKETPLACE_V3_LOADS_GRID.mobilePageSize
        : MARKETPLACE_V3_LOADS_GRID.desktopPageSize;

    const [viewMoreClickCount, setViewMoreClickCount] = useState(0);

    const queryParams = useMemo<LoadMarketParam>(
        () => ({
            preloads: ["relevancy_tags"],
            state_id: marketplaceConfig?.srcStateId,
            destination_state_id: marketplaceConfig?.destStateIds,
            lorry_type_id: marketplaceConfig?.lorryTypeIds,
            l: pageSize,
            c_i: Number(companyId) || undefined
        }),
        [pageSize, marketplaceConfig?.destStateIds, marketplaceConfig?.lorryTypeIds, marketplaceConfig?.srcStateId, companyId]
    );

    const filterKey = useMemo(
        () =>
            [
                marketplaceConfig?.srcStateId,
                marketplaceConfig?.destStateIds?.join(","),
                marketplaceConfig?.lorryTypeIds?.join(",")
            ].join("|"),
        [marketplaceConfig?.destStateIds, marketplaceConfig?.lorryTypeIds, marketplaceConfig?.srcStateId]
    );

    useEffect(() => {
        setViewMoreClickCount(0);
    }, [filterKey]);

    useEffect(() => {
        requestAnimationFrame(() => scrollToElementById(MARKETPLACE_V3_LOADS_SECTION_ID, { focus: false }));
    }, []);

    const { data, isFetched, isFetching, isFetchingNextPage, fetchNextPage, hasNextPage } =
        useGetLoadMarketInfiniteQueryNew(queryParams, {
            refetchOnWindowFocus: false,
            keepPreviousData: true,
            retry: 0,
            onSuccess() {
                setShouldExecuteApi(false);
            },
            onError() {
                setShouldExecuteApi(false);
            },
            enabled: shouldExecuteApi === true
        });

    const loadsData = useMemo(() => data?.pages?.flatMap((page) => page?.data?.load_cards ?? []) ?? [], [data?.pages]);

    const handleViewMore = () => {
        const isLoggedIn = Boolean(companyId && isPhoneVerified);
        const loginRequired = viewMoreClickCount >= MARKETPLACE_V3_LOADS_GRID.viewMoreClicksBeforeLogin && !isLoggedIn;

        if (loginRequired) {
            setShouldPreventLayoutSwitch?.(true);
            router.push({ hash: "Login" }, undefined, { shallow: true });
            return;
        }

        trackEvent?.(GA4EventNames.WEB_V2.view_more_loads_clicked, eventData);
        setViewMoreClickCount((count) => count + 1);
        fetchNextPage();
    };

    const isLoadsPending =
        shouldExecuteApi === undefined || (isFetching && !isFetchingNextPage && loadsData.length === 0);
    const showEmptyState = !isLoadsPending && isFetched && loadsData.length === 0;

    return (
        <HomepageV2SectionCard variant="white" id={MARKETPLACE_V3_LOADS_SECTION_ID}>
            <div className={SectionStyles.container} key={filterKey}>
                {!showEmptyState ? (
                    <div className={SectionStyles.header}>
                        <HomepageV2Animate variant="fade-right">
                            <h2 className={SectionStyles.title}>
                                <span className={SectionStyles.titleLine}>{t("loads.titleLine")}</span>
                                <span className={SectionStyles.titleHighlight}>{t("loads.titleHighlight")}</span>
                            </h2>
                            {!isPhoneVerified && titleH1Desc ? <p className={Styles.shortDesc}>{titleH1Desc}</p> : null}
                        </HomepageV2Animate>
                    </div>
                ) : null}

                {showEmptyState ? (
                    <MarketplaceV3NoLoadsEmptyState trackEvent={trackEvent} eventData={eventData} />
                ) : (
                    <>
                        <AvailableLoadsGrid
                            loads={loadsData}
                            cardIdPrefix="marketplace-v3"
                            onBidNow={onBidNow}
                            showPostingNudge
                            isLoading={isLoadsPending}
                            shimmerCount={pageSize}
                        />

                        {hasNextPage ? (
                            <HomepageV2Animate variant="fade-up" delay={180}>
                                <div className={SectionStyles.viewMoreWrap}>
                                    <button
                                        type="button"
                                        className={SectionStyles.viewMoreBtn}
                                        onClick={handleViewMore}
                                        disabled={isFetchingNextPage}
                                    >
                                        {t("loads.viewMore")}
                                        <ArrowRightGrey />
                                    </button>
                                </div>
                            </HomepageV2Animate>
                        ) : null}
                    </>
                )}
            </div>
        </HomepageV2SectionCard>
    );
};

export default MarketplaceV3LoadsSection;
